import React from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MicroRingChart } from "./MicroRingChart";
import { colorSystem } from "../../theme";
import { StackedMicroRing } from "./StackedMicroRing";
import useTheme from "@mui/styles/useTheme";
import { InternalNutritionEntryDto } from "../../utils/chart";
import dayjs from "dayjs";
import ClientNutritionTableRow from "./ClientNutritionTableRow";
import { thousandSeparator } from "./utils/thousandSeparator";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    margin: theme.spacing(0),
    padding: theme.spacing(0, 2, 2, 2),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0),
      margin: theme.spacing(-0.5, 0, 0, 0),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(0),
      margin: theme.spacing(-0.5, 0, 0, 0),
    },
  },

  table: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    maxWidth: "100%",
  },

  tableBody: {},

  tableRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    width: "100%",

    padding: theme.spacing(2, 3, 2, 3),

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },

    [theme.containerQueries.down("md")]: {
      padding: theme.spacing(1, 0, 1, 2),
    },
  },

  tableCell: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderBottom: "none",
    textWrap: "nowrap",
    width: "50%",
  },
}));

const TableCellWithChart = ({
  children,
  value,
  color,
}: {
  children: React.ReactNode;
  value: number;
  color?: string;
}) => {
  const s = useStyles();

  return (
    <Box className={s.tableCell}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: -2 }}>
        <Box sx={{ width: "max-content" }}>
          <MicroRingChart value={value} color={color} />
        </Box>
        {children}
      </Box>
    </Box>
  );
};

const MacroTableCell = ({
  children,
  value,
  target,
  color,
  borderBottom,
  disableTag,
}: {
  children: React.ReactNode;
  value: number;
  target: number;
  color?: string;
  borderBottom?: boolean;
  disableTag?: boolean;
}) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <TableCellWithChart value={target ? value / target : 1} color={color}>
      {children}{" "}
      {!disableTag && (
        <Typography
          sx={{ marginTop: 0.5, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
          variant="subtitle2"
          color={color}
        >
          {thousandSeparator(value)} g
        </Typography>
      )}
    </TableCellWithChart>
  );
};

interface ClientNutritionCardTableNoTargetProps {
  data: InternalNutritionEntryDto;
  showAverageIntake?: boolean;
  disableTags?: boolean;
}

export const ClientNutritionCardTableNoTarget = (
  props: ClientNutritionCardTableNoTargetProps,
) => {
  const { data, showAverageIntake, disableTags } = props;
  const s = useStyles();
  const theme = useTheme();

  const smUp = useMediaQuery(theme.breakpoints.up("sm"));

  const isToday = dayjs().isSame(data?.date, "day");

  const mainSectionRows = [
    {
      title: "Protein",
      field: "protein",
      color: colorSystem.nutritionProteins,
      coeff: 4,
    },
    {
      title: "Carbs",
      field: "carbs",
      color: colorSystem.nutritionCarbs,
      coeff: 4,
    },
    { title: "Fat", field: "fat", color: colorSystem.nutritionFats, coeff: 9 },
  ];

  const calculatedCalories = mainSectionRows.map(
    (row) => data && data[row.field] * row.coeff,
  );

  const totalCalculatedCalories = Math.round(
    calculatedCalories.reduce((acc, val) => acc + val, 0),
  );

  const caloriesRemaining =
    data &&
    Math.round((data.targetCalories - totalCalculatedCalories) * 10) / 10;

  return (
    <Box className={s.root}>
      <Box className={s.table}>
        <Box className={s.tableRow}>
          <TableCellWithChart value={0}>
            <Typography variant="subtitle1" color="textSecondary">
              MACRO
            </Typography>
          </TableCellWithChart>
          <Box className={s.tableCell}>
            <Typography variant="subtitle1" color="textSecondary" align="left">
              {showAverageIntake ? "AVG. INTAKE" : "CURRENT"}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ width: "100%", my: 1 }} />
        {mainSectionRows.map((row) => {
          return (
            <ClientNutritionTableRow
              key={row.field}
              value={data ? data[row.field] : 0}
              color={row.color}
              title={row.title}
              hideTarget
              showAverageIntake={showAverageIntake}
            />
          );
        })}
        <Divider sx={{ width: "100%", my: 1 }} />
        <Box className={s.tableRow}>
          <Box className={s.tableCell}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: -2 }}
            >
              <Box sx={{ width: "max-content" }}>
                <StackedMicroRing
                  values={calculatedCalories.map((calories, index) =>
                    data && data.targetCalories
                      ? calories / data.targetCalories
                      : 0,
                  )}
                  colors={mainSectionRows.map((row) => row.color)}
                />
              </Box>
              <Typography variant="subtitle1">Calories</Typography>
            </Box>
          </Box>
          <Box className={s.tableCell}>
            <Typography variant="subtitle1" align="left">
              {data ? thousandSeparator(data.calories) : 0} kcal
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
